import * as L from "leaflet";
import React, { Component } from "react";
import "leaflet/dist/leaflet.css";
import "leaflet-kml";
import { forceRenderStyles, style } from "typestyle";
import { GeoJsonObject } from "geojson";

export type FlightPlanPageMapProps = {
  flightPlanKml: string;
  baseLayerUrl: string;
  siteBoundary: GeoJsonObject;
};

export default class FlightPlanPageMap extends Component<FlightPlanPageMapProps> {
  el: HTMLDivElement | null;
  map: L.Map;
  baseLayer: L.TileLayer;
  flightPlanLayer: L.GeoJSON;
  siteBoundaryLayer: L.GeoJSON;

  async componentDidMount(): Promise<void> {
    if (!this.el) {
      throw new Error("Could not get map element");
    }

    // Force stretching map container to render leaflet map properly
    forceRenderStyles();

    this.map = L.map(this.el);

    // wait until map is inserted into DOM tree properly
    await waitRenderCycle();

    this.updateKmlLayer();
    this.updateBaselayer();
    this.updateSiteBoundaryLayer();
  }

  componentDidUpdate(prevProps: FlightPlanPageMapProps): void {
    // prevent base layer blinking on buffer switch
    if (prevProps.baseLayerUrl !== this.props.baseLayerUrl) {
      this.updateBaselayer();
    }
    this.updateSiteBoundaryLayer();
    this.updateKmlLayer();
  }

  render(): JSX.Element {
    return (
      <div
        className={style({
          position: "absolute",
          zIndex: 1,
          top: "0",
          bottom: "0",
          left: "0",
          right: "0",
        })}
        ref={(el) => (this.el = el)}
      />
    );
  }

  private updateKmlLayer() {
    if (this.flightPlanLayer) {
      this.map.removeLayer(this.flightPlanLayer);
    }

    const { flightPlanKml } = this.props;

    const parser = new DOMParser();
    const kmlDocument = parser.parseFromString(flightPlanKml, "text/xml");
    // @ts-ignore
    this.flightPlanLayer = new L.KML(kmlDocument);
    this.map.addLayer(this.flightPlanLayer);
    this.map.fitBounds(this.flightPlanLayer.getBounds());
  }

  private updateBaselayer() {
    if (this.baseLayer) {
      this.map.removeLayer(this.baseLayer);
    }
    this.baseLayer = L.tileLayer(this.props.baseLayerUrl);
    this.map.addLayer(this.baseLayer);
  }

  private updateSiteBoundaryLayer() {
    if (this.siteBoundaryLayer) {
      this.map.removeLayer(this.siteBoundaryLayer);
    }
    this.siteBoundaryLayer = L.geoJSON(this.props.siteBoundary);
    this.map.addLayer(this.siteBoundaryLayer);
  }
}

const waitRenderCycle = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
